import React, { useEffect, useState } from 'react'
import { X, Check, XCircle } from 'lucide-react'
import { adminApi } from '../../services/api'
import { useColors } from '../../hooks/useColors'
import { useToast } from '../../contexts/ToastContext'
import { useSocket } from '../../hooks/useSocket'
import LoadErrorState from '../common/LoadErrorState'
import { useAuthStore } from '../../store/authStore'

interface AgentRow {
  id: string
  name?: string
  phone?: string
  kyc_status?: 'pending' | 'verified' | 'rejected'
}

interface KycDocument {
  id: string
  doc_type: string
  file_url: string
  mime_type: string
  size_bytes: number
  uploaded_at: string
  review_status: 'pending' | 'approved' | 'rejected'
  review_notes?: string | null
  reviewed_at?: string | null
  reviewed_by_name?: string | null
  signed_get_url: string
}

type RetryAction = (() => Promise<void>) | null

const REQUIRED_DOCS = [
  { id: 'aadhaar_front', label: 'Aadhaar (Front)' },
  { id: 'aadhaar_back', label: 'Aadhaar (Back)' },
  { id: 'pan', label: 'PAN Card' },
  { id: 'driving_license_front', label: 'Driving License (Front)' },
  { id: 'driving_license_back', label: 'Driving License (Back)' },
  { id: 'selfie', label: 'Selfie' },
  { id: 'vehicle_rc', label: 'Vehicle RC' },
  { id: 'vehicle_insurance', label: 'Vehicle Insurance' },
]

const withTenSecondTimeout = async <T,>(request: Promise<T>): Promise<T> => {
  let timeoutId: number | undefined
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = window.setTimeout(() => reject(new Error("Couldn't load documents. Try again.")), 10000)
  })

  try {
    return await Promise.race([request, timeout])
  } finally {
    if (timeoutId !== undefined) window.clearTimeout(timeoutId)
  }
}

export default function AgentKycReviewDrawer({
  agent,
  onClose,
  onUpdate,
}: {
  agent: AgentRow
  onClose: () => void
  onUpdate: () => void
}) {
  const colors = useColors()
  const { addToast } = useToast()
  const user = useAuthStore((state) => state.user)
  const [documents, setDocuments] = useState<KycDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [patchError, setPatchError] = useState<string | null>(null)
  const [retryAction, setRetryAction] = useState<RetryAction>(null)
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null)
  const [rejectingDocId, setRejectingDocId] = useState<string | null>(null)
  const [reviewNotes, setReviewNotes] = useState('')

  const fetchDocs = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    setPatchError(null)
    setRetryAction(null)
    try {
      const response = await withTenSecondTimeout(adminApi.getAgentKycDocuments(agent.id))
      setDocuments(response.data || [])
    } catch {
      setError("Couldn't load documents. Try again.")
      addToast('Failed to load KYC documents', 'error')
      
    } finally {
      setLoading(false)
    }
  }, [agent.id, addToast])

  useEffect(() => {
    void fetchDocs()
  }, [fetchDocs])

  useSocket({
    'agent:kyc_doc_uploaded': (data: { agent_id: string }) => {
      if (data.agent_id === agent.id) {
        addToast('New document uploaded by agent', 'info')
        void fetchDocs()
      }
    }
  })

  const getErrorMessage = (err: unknown, fallback: string) => {
    if (err && typeof err === 'object') {
      const maybe = err as {
        response?: { data?: { message?: string } }
        data?: { message?: string }
        message?: string
      }
      return maybe?.response?.data?.message || maybe?.data?.message || maybe?.message || fallback
    }
    if (err instanceof Error) return err.message || fallback
    return fallback
  }

  const applyLocalReview = (docId: string, status: 'approved' | 'rejected', notes?: string) => {
    const reviewedAt = new Date().toISOString()
    setDocuments((prev) => prev.map((doc) => (
      doc.id === docId
        ? {
            ...doc,
            review_status: status,
            review_notes: status === 'rejected' ? notes || null : null,
            reviewed_at: reviewedAt,
            reviewed_by_name: user?.name || doc.reviewed_by_name || 'You',
          }
        : doc
    )))
  }

  const handleReview = async (docId: string, status: 'approved' | 'rejected', notes?: string) => {
    const trimmedNotes = notes?.trim()
    if (status === 'rejected' && (!trimmedNotes || trimmedNotes.length < 10)) {
      addToast('Please provide at least 10 characters for rejection notes', 'error')
      return
    }
    const reviewPayload = { review_status: status, review_notes: status === 'rejected' ? trimmedNotes : undefined }
    try {
      setPatchError(null)
      setRetryAction(null)
      await adminApi.reviewAgentKycDocument(agent.id, docId, reviewPayload)
      applyLocalReview(docId, status, trimmedNotes)
      addToast(`Document ${status}`, 'success')
      setRejectingDocId(null)
      setReviewNotes('')
      onUpdate()
    } catch (err: unknown) {
      const message = getErrorMessage(err, `Failed to ${status} document`)
      setPatchError(message)
      setRetryAction(() => () => handleReview(docId, status, trimmedNotes))
      addToast(message, 'error')
    }
  }

  const handleVerifyAll = async () => {
    const uploadedDocs = REQUIRED_DOCS.map((reqDoc) => documents.find((doc) => doc.doc_type === reqDoc.id))
    if (uploadedDocs.some((doc) => !doc)) {
      addToast('All 8 documents must be uploaded before verifying all', 'error')
      return
    }
    const pendingDocs = (uploadedDocs as KycDocument[]).filter((d) => d.review_status !== 'approved')
    if (pendingDocs.length === 0) {
      addToast('No pending documents to approve', 'info')
      return
    }

    try {
      setPatchError(null)
      setRetryAction(null)
      for (const doc of pendingDocs) {
        await adminApi.reviewAgentKycDocument(agent.id, doc.id, { review_status: 'approved' })
        applyLocalReview(doc.id, 'approved')
      }
      await adminApi.approveAgentKyc(agent.id, 'verified', false)
      addToast('All documents approved', 'success')
      onUpdate()
      onClose()
    } catch (err: unknown) {
      const message = getErrorMessage(err, 'Failed to approve all documents')
      setPatchError(message)
      setRetryAction(() => handleVerifyAll)
      addToast(message, 'error')
    }
  }

  const docsByType = React.useMemo(() => {
    const map = new Map<string, KycDocument>()
    documents.forEach((doc) => map.set(doc.doc_type, doc))
    return map
  }, [documents])

  const allRequiredUploaded = REQUIRED_DOCS.every((reqDoc) => docsByType.has(reqDoc.id))
  const hasPendingOrRejected = REQUIRED_DOCS.some((reqDoc) => {
    const doc = docsByType.get(reqDoc.id)
    return doc ? doc.review_status === 'pending' || doc.review_status === 'rejected' : false
  })
  const canVerifyAll = allRequiredUploaded && hasPendingOrRejected

  return (
    <>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 70,
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0,0,0,0.3)',
          }}
          onClick={onClose}
        />
        <div
          style={{
            position: 'relative',
            width: '50%',
            minWidth: '500px',
            height: '100%',
            background: colors.bgCard,
            boxShadow: '-8px 0 32px rgba(0,0,0,0.08)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div style={{ padding: '24px 28px', borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <h3 className="font-display" style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: colors.textPrimary }}>
                  {agent.name || 'Agent'} KYC Review
                </h3>
                <span className="font-body" style={{
                  borderRadius: '999px', padding: '4px 10px', fontSize: '11px', fontWeight: 600,
                  background: agent.kyc_status === 'verified' ? '#F0FDF4' : agent.kyc_status === 'rejected' ? '#FEF2F2' : 'rgba(214,185,123,0.12)',
                  color: agent.kyc_status === 'verified' ? '#15803D' : agent.kyc_status === 'rejected' ? '#991B1B' : '#D6B97B',
                  border: `1px solid ${agent.kyc_status === 'verified' ? '#86EFAC' : agent.kyc_status === 'rejected' ? '#FCA5A5' : '#D6B97B'}`,
                  textTransform: 'capitalize'
                }}>
                  {agent.kyc_status || 'pending'}
                </span>
              </div>
              <p className="font-body" style={{ margin: '4px 0 0', fontSize: '13px', color: colors.textMuted }}>
                {agent.phone}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              {canVerifyAll ? (
                <button
                  type="button"
                  onClick={() => void handleVerifyAll()}
                  style={{ height: '32px', borderRadius: '8px', border: 'none', background: '#15803D', color: '#fff', fontSize: '12px', fontWeight: 600, padding: '0 16px', cursor: 'pointer' }}
                >
                  Verify all & approve agent
                </button>
              ) : null}
              <button
                type="button"
                onClick={onClose}
                style={{ width: '32px', height: '32px', borderRadius: '8px', border: `1px solid ${colors.border}`, background: colors.bgCard, cursor: 'pointer', display: 'grid', placeItems: 'center' }}
              >
                <X size={18} color={colors.textSecondary} />
              </button>
            </div>
          </div>

          {/* Body */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>
            {error ? (
              <LoadErrorState message={error} onRetry={() => void fetchDocs()} />
            ) : loading ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
                {REQUIRED_DOCS.map((doc) => (
                  <div key={doc.id} style={{ height: '230px', borderRadius: '12px', background: colors.bgSecondary, border: `1px solid ${colors.border}`, opacity: 0.7 }} />
                ))}
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '16px' }}>
                {patchError ? (
                  <div style={{ border: '1px solid #FCA5A5', background: '#FEF2F2', color: '#991B1B', borderRadius: '10px', padding: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                    <span className="font-body" style={{ fontSize: '13px', fontWeight: 600 }}>{patchError}</span>
                    {retryAction ? (
                      <button type="button" onClick={() => void retryAction()} style={{ border: '1px solid #991B1B', background: 'transparent', color: '#991B1B', borderRadius: '6px', padding: '6px 10px', fontWeight: 700, cursor: 'pointer' }}>
                        Retry
                      </button>
                    ) : null}
                  </div>
                ) : null}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
                {REQUIRED_DOCS.map((reqDoc) => {
                  const doc = docsByType.get(reqDoc.id)

                  return (
                    <div key={reqDoc.id} style={{ border: `1px solid ${colors.border}`, borderRadius: '12px', padding: '16px', background: colors.bgSecondary }}>
                      <p className="font-body" style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 600, color: colors.textPrimary }}>
                        {reqDoc.label}
                      </p>

                      {!doc ? (
                        <div style={{ height: '140px', background: colors.surface, borderRadius: '8px', display: 'grid', placeItems: 'center', border: `1px dashed ${colors.border}` }}>
                          <p className="font-body" style={{ fontSize: '13px', color: colors.textMuted, margin: 0 }}>Not uploaded</p>
                        </div>
                      ) : (
                        <div>
                          <div
                            onClick={() => setLightboxUrl(doc.signed_get_url)}
                            style={{ height: '140px', borderRadius: '8px', overflow: 'hidden', cursor: 'pointer', background: '#e5e7eb', marginBottom: '12px' }}
                          >
                            {doc.mime_type === 'application/pdf' ? (
                              <div style={{ height: '100%', display: 'grid', placeItems: 'center' }}>
                                <p className="font-body" style={{ fontSize: '14px', fontWeight: 600 }}>PDF Document</p>
                                <a className="font-body" href={doc.signed_get_url} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()} style={{ fontSize: '12px', color: '#2563EB' }}>Open preview</a>
                              </div>
                            ) : (
                              <img src={doc.signed_get_url} alt={reqDoc.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            )}
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span className="font-body" style={{
                                borderRadius: '999px', padding: '2px 8px', fontSize: '10px', fontWeight: 600, textTransform: 'capitalize',
                                background: doc.review_status === 'approved' ? '#F0FDF4' : doc.review_status === 'rejected' ? '#FEF2F2' : 'rgba(214,185,123,0.12)',
                                color: doc.review_status === 'approved' ? '#15803D' : doc.review_status === 'rejected' ? '#991B1B' : '#D6B97B',
                              }}>
                                {doc.review_status}
                              </span>
                              <span className="font-body" style={{ fontSize: '11px', color: colors.textMuted }}>
                                {new Date(doc.uploaded_at).toLocaleString()}
                                {doc.reviewed_at ? ` • reviewed ${new Date(doc.reviewed_at).toLocaleString()}` : ''}
                                {doc.reviewed_by_name ? ` • by ${doc.reviewed_by_name}` : ''}
                              </span>
                            </div>
                            <span className="font-body" style={{ fontSize: '11px', color: colors.textMuted }}>
                              {Math.round(doc.size_bytes / 1024)} KB
                            </span>
                          </div>

                          {rejectingDocId === doc.id ? (
                            <div>
                              <textarea
                                value={reviewNotes}
                                onChange={(e) => setReviewNotes(e.target.value)}
                                placeholder="Rejection notes (min 10 chars)"
                                maxLength={500}
                                style={{ width: '100%', boxSizing: 'border-box', height: '60px', padding: '8px', borderRadius: '6px', border: `1px solid ${colors.border}`, fontSize: '12px', marginBottom: '8px', fontFamily: 'inherit' }}
                              />
                              <p className="font-body" style={{ margin: '0 0 8px', fontSize: '11px', color: colors.textMuted, textAlign: 'right' }}>{reviewNotes.length}/500</p>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                  type="button"
                                  disabled={reviewNotes.trim().length < 10}
                                  onClick={() => void handleReview(doc.id, 'rejected', reviewNotes)}
                                  style={{
                                    flex: 1, height: '28px', background: reviewNotes.trim().length < 10 ? '#fca5a5' : '#991B1B',
                                    color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px',
                                    cursor: reviewNotes.trim().length < 10 ? 'not-allowed' : 'pointer'
                                  }}>Confirm Reject</button>
                                <button type="button" onClick={() => setRejectingDocId(null)} style={{ flex: 1, height: '28px', background: 'transparent', color: colors.textSecondary, border: `1px solid ${colors.border}`, borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>Cancel</button>
                              </div>
                            </div>
                          ) : doc.review_status === 'approved' ? (
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <div style={{ flex: 1, height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', background: '#F0FDF4', color: '#15803D', border: '1px solid #86EFAC', borderRadius: '6px', fontSize: '12px', fontWeight: 700 }}>
                                <Check size={14} /> Approved
                              </div>
                              <button type="button" onClick={() => setRejectingDocId(doc.id)} style={{ height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', padding: '0 12px', background: '#FEF2F2', color: '#991B1B', border: '1px solid #FCA5A5', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                                <XCircle size={14} /> Reject
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button type="button" onClick={() => void handleReview(doc.id, 'approved')} style={{ flex: 1, height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', background: '#F0FDF4', color: '#15803D', border: '1px solid #86EFAC', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                                <Check size={14} /> Approve
                              </button>
                              <button type="button" onClick={() => setRejectingDocId(doc.id)} style={{ flex: 1, height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', background: '#FEF2F2', color: '#991B1B', border: '1px solid #FCA5A5', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                                <XCircle size={14} /> Reject
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {lightboxUrl && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(0,0,0,0.9)', display: 'grid', placeItems: 'center', padding: '40px'
          }}
          onClick={() => setLightboxUrl(null)}
        >
          <button style={{ position: 'absolute', top: '20px', right: '20px', background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}>
            <X size={32} />
          </button>
          {lightboxUrl.includes('.pdf') || lightboxUrl.includes('response-content-disposition=inline') ? (
             <iframe src={lightboxUrl} style={{ width: '80%', height: '90%', background: '#fff', border: 'none', borderRadius: '8px' }} />
          ) : (
            <img src={lightboxUrl} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} alt="Lightbox" />
          )}
        </div>
      )}
    </>
  )
}
